// One customer-facing status vocabulary for the feature request page, the support
// portal and the widget, so a label reads the same on every surface.

// Raw Linear status types and status names, normalized to a `roadmap.status.*` key.
const STATUS_TRANSLATION_KEYS: Record<string, string> = {
	started: "inProgress",
	"in progress": "inProgress",
	planned: "planned",
	backlog: "backlog",
	triage: "triage",
	"under consideration": "triage",
	review: "inReview",
	"in review": "inReview",
	"under review": "inReview",
	completed: "completed",
	unstarted: "unstarted",
	canceled: "canceled",
	paused: "paused",
	todo: "todo",
	"to do": "todo",
}

// Mirrors the English `roadmap.status.*` translations. Server-rendered portal
// pages fall back to these before the i18n bundle loads, so a value that drifts
// from `en/common.json` shows up as a React hydration mismatch.
export const STATUS_FALLBACK_LABELS: Record<string, string> = {
	inProgress: "In Progress",
	planned: "Planned",
	backlog: "Backlog",
	triage: "Under consideration",
	inReview: "In Review",
	completed: "Completed",
	unstarted: "Unstarted",
	canceled: "Canceled",
	paused: "Paused",
	todo: "To do",
}

export type Translator = (key: string, fallback: string) => string

export const getStatusTranslationKey = (status: string): string =>
	STATUS_TRANSLATION_KEYS[status.trim().toLowerCase()] ?? status

// Unknown values pass through untouched: they are a workspace's own Linear status
// names, which are already cased the way that workspace wants to read them.
export const getStatusFallbackLabel = (
	status: string,
	unknownFallback?: string,
): string =>
	STATUS_FALLBACK_LABELS[getStatusTranslationKey(status)] ??
	unknownFallback ??
	status

// A workspace's own Linear status name wins whenever it isn't one we know, so
// "Shipping soon" stays "Shipping soon" while "Triage" reads as the friendlier label.
export const getStatusLabel = (
	status: string,
	t?: Translator,
	unknownFallback?: string,
): string => {
	const fallback = getStatusFallbackLabel(status, unknownFallback)
	return t
		? t(`roadmap.status.${getStatusTranslationKey(status)}`, fallback)
		: fallback
}
